import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  collection, 
  doc, 
  query, 
  where,
  updateDoc,
  onSnapshot,
  Unsubscribe,
  DocumentData
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAppState } from './useAppState';

interface TooltipPackage {
  id: string;
  name: string;
  expert: string;
  date: string;
  amount: string;
  packageId: string;
  creatorId: string;
  price: number;
  createdAt: string;
  purchasedAt?: string;
  purchaseDocId?: string;
  rating?: number;
  review?: string;
  primaryUrl?: string;
  description?: string;
  selected?: boolean;
}

interface ProfileData {
  name: string;
  profileImageUrl?: string;
}

interface PackageRating {
  avgRating: number;
  totalReviews: number;
}

export const useTooltipData = () => {
  const { user } = useAppState();
  const [data, setData] = useState<TooltipPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageRatings, setPackageRatings] = useState<{[key: string]: PackageRating}>({});
  
  // Real-time data stores
  const [profiles, setProfiles] = useState<{[key: string]: ProfileData}>({});
  const [packages, setPackages] = useState<{[key: string]: DocumentData}>({});
  const [purchases, setPurchases] = useState<DocumentData[]>([]);
  const [userSelections, setUserSelections] = useState<string[]>([]);
  
  const unsubscribeRefs = useRef<Unsubscribe[]>([]);

  // Helper: parse Firestore Timestamp-like values into JS Date
  const parseFirestoreTimestamp = useCallback((value: unknown): Date | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'object') {
      const v = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number };

      // Firestore Timestamp-like object with toDate()
      if (typeof v.toDate === 'function') {
        try {
          return v.toDate();
        } catch {
          // fall through
        }
      }

      // Object with seconds/nanoseconds
      if (typeof v.seconds === 'number') {
        const secs = v.seconds || 0;
        const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds : 0;
        return new Date(secs * 1000 + Math.floor(nanos / 1e6));
      }
    }
    // If it's a number, treat as milliseconds or seconds (heuristic)
    if (typeof value === 'number') {
      // if value looks like seconds (10 digits) convert to ms
      if (value < 1e12) {
        return new Date(value * 1000);
      }
      return new Date(value);
    }
    // If it's a string, try to parse
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
    return null;
  }, []);

  // Real-time listener for user's tooltip purchases
  const setupPurchasesListener = useCallback(() => {
    if (!user?.uid) return null;

    const purchasesRef = collection(db, 'tooltipPurchases');
    const purchaseQuery = query(
      purchasesRef,
      where('purchaserId', '==', user.uid)
    );

    return onSnapshot(
      purchaseQuery,
      (snapshot) => {
        const purchaseData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as (DocumentData & { id: string; purchasedAt?: unknown })[];
        
        // Sort by purchase date (most recent first)
        purchaseData.sort((a, b) => {
          const dateA = parseFirestoreTimestamp(a.purchasedAt) || new Date(0);
          const dateB = parseFirestoreTimestamp(b.purchasedAt) || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        
        setPurchases(purchaseData);
        console.log('Real-time purchases updated:', purchaseData.length);
      },
      (error) => {
        console.error('Error in purchases listener:', error);
        setError('Failed to fetch purchase data');
      }
    );
  }, [user?.uid, parseFirestoreTimestamp]);

  // Real-time listener for tooltip packages
  const setupPackagesListener = useCallback(() => {
    const packagesRef = collection(db, 'tooltipPackages');
    
    return onSnapshot(
      packagesRef,
      (snapshot) => {
        const packageMap: {[key: string]: DocumentData} = {};
        snapshot.docs.forEach(doc => {
          packageMap[doc.id] = {
            id: doc.id,
            ...doc.data()
          };
        });
        setPackages(packageMap);
        console.log('Real-time packages updated:', snapshot.docs.length);
      },
      (error) => {
        console.error('Error in packages listener:', error);
        setError('Failed to fetch package data');
      }
    );
  }, []);

  // Real-time listener for profiles
  const setupProfilesListener = useCallback(() => {
    const profilesRef = collection(db, 'profiles');
    
    return onSnapshot(
      profilesRef,
      (snapshot) => {
        const profileMap: {[key: string]: ProfileData} = {};
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.userId) {
            profileMap[data.userId] = {
              name: data.name || 'Unknown Expert',
              profileImageUrl: data.profileImageUrl
            };
          }
        });
        setProfiles(profileMap);
        console.log('Real-time profiles updated:', snapshot.docs.length);
      },
      (error) => {
        console.error('Error in profiles listener:', error);
      }
    );
  }, []);

  // Real-time listener for user selections
  const setupUserSelectionsListener = useCallback(() => {
    if (!user?.uid) return null;

    const userDocRef = doc(db, 'users', user.uid);
    
    return onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          const selectedPackages = userData.selectedPackages || [];
          setUserSelections(selectedPackages);
          console.log('Real-time user selections updated:', selectedPackages.length);
        }
      },
      (error) => {
        console.error('Error in user selections listener:', error);
      }
    );
  }, [user?.uid]);

  // Real-time listener for package ratings
  const setupRatingsListener = useCallback((packageIds: string[]) => {
    if (packageIds.length === 0) return [];
    
    const unsubscribes: Unsubscribe[] = [];
    
    packageIds.forEach(packageId => {
      const purchasesRef = collection(db, 'tooltipPurchases');
      const ratingsQuery = query(
        purchasesRef,
        where('packageId', '==', packageId)
      );
      
      const unsubscribe = onSnapshot(
        ratingsQuery,
        (snapshot) => {
          const ratings: number[] = [];
          
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (
              data.rating !== undefined &&
              data.rating !== null &&
              typeof data.rating === 'number' &&
              data.rating > 0 &&
              data.rating <= 5
            ) {
              ratings.push(data.rating);
            }
          });
          
          const avgRating = ratings.length > 0
            ? Number((ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1))
            : 0;
          
          const totalReviews = ratings.length;
          
          setPackageRatings(prev => ({
            ...prev,
            [packageId]: {
              avgRating,
              totalReviews
            }
          }));
        },
        (error) => {
          console.error(`Error in ratings listener for package ${packageId}:`, error);
        }
      );
      
      unsubscribes.push(unsubscribe);
    });
    
    return unsubscribes;
  }, []);

  // Function to combine all real-time data into final tooltip packages
  const combineDataToTooltipPackages = useCallback(() => {
    if (purchases.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    const tooltipPackages: TooltipPackage[] = [];

    purchases.forEach(purchase => {
      const packageData = packages[purchase.packageId];
      if (!packageData) return;

      // Get creator profile with fallback
      const creatorProfile = profiles[packageData.creatorId] || { 
        name: 'Unknown Expert' 
      };

      // Parse dates
      const purchaseDate = parseFirestoreTimestamp(purchase.purchasedAt) || new Date();
      const formattedDate = purchaseDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      // Format amount
      const formattedAmount = `$${(packageData.price || 0).toFixed(2)}`;

      // Check if package is selected
      const isSelected = userSelections.includes(purchase.packageId);

      const tooltipPackage: TooltipPackage = {
        id: purchase.packageId,
        name: packageData.name || 'Unnamed Package',
        expert: creatorProfile.name,
        date: formattedDate,
        amount: formattedAmount,
        packageId: purchase.packageId,
        creatorId: packageData.creatorId || '',
        price: packageData.price || 0,
        createdAt: (parseFirestoreTimestamp(packageData.createdAt) || new Date()).toISOString(),
        purchasedAt: purchaseDate.toISOString(),
        purchaseDocId: purchase.id,
        rating: purchase.rating || 0,
        review: purchase.review || '',
        primaryUrl: packageData.primaryUrl || packageData.primary_url || undefined,
        description: packageData.description || '',
        selected: isSelected
      };

      tooltipPackages.push(tooltipPackage);
    });

    // Sort by purchase date (most recent first)
    tooltipPackages.sort((a, b) => {
      const dateA = new Date(a.purchasedAt || a.createdAt);
      const dateB = new Date(b.purchasedAt || b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });

    setData(tooltipPackages);
    setLoading(false);
    setError(null);

    // Setup ratings listeners for all packages
    const packageIds = tooltipPackages.map(pkg => pkg.packageId);
    const ratingsUnsubscribes = setupRatingsListener(packageIds);
    unsubscribeRefs.current.push(...ratingsUnsubscribes);

    console.log('Final tooltip packages combined:', tooltipPackages.length);
  }, [purchases, packages, profiles, userSelections, parseFirestoreTimestamp, setupRatingsListener]);

  // Setup all real-time listeners
  const setupRealtimeListeners = useCallback(() => {
    if (!user?.uid) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Clean up any existing listeners
    unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
    unsubscribeRefs.current = [];

    try {
      // Setup all listeners
      const purchasesUnsubscribe = setupPurchasesListener();
      const packagesUnsubscribe = setupPackagesListener();
      const profilesUnsubscribe = setupProfilesListener();
      const userSelectionsUnsubscribe = setupUserSelectionsListener();

      // Add to cleanup array
      if (purchasesUnsubscribe) unsubscribeRefs.current.push(purchasesUnsubscribe);
      if (packagesUnsubscribe) unsubscribeRefs.current.push(packagesUnsubscribe);
      if (profilesUnsubscribe) unsubscribeRefs.current.push(profilesUnsubscribe);
      if (userSelectionsUnsubscribe) unsubscribeRefs.current.push(userSelectionsUnsubscribe);

      console.log('All real-time listeners setup successfully');
    } catch (error) {
      console.error('Error setting up real-time listeners:', error);
      setError('Failed to setup real-time updates');
      setLoading(false);
    }
  }, [user?.uid, setupPurchasesListener, setupPackagesListener, setupProfilesListener, setupUserSelectionsListener]);

  // Combine data whenever any real-time data changes
  useEffect(() => {
    combineDataToTooltipPackages();
  }, [combineDataToTooltipPackages]);

  // Function to save rating and review (optimized for real-time updates)
  const saveRatingAndReview = useCallback(async (purchaseDocId: string, rating: number, review: string) => {
    if (!purchaseDocId) {
      throw new Error('Purchase document ID is required');
    }

    try {
      const purchaseDocRef = doc(db, 'tooltipPurchases', purchaseDocId);
      await updateDoc(purchaseDocRef, {
        rating: rating,
        review: review,
        reviewedAt: new Date()
      });

      // Real-time listeners will automatically update the data
      console.log('Rating and review saved successfully');
      return true;
    } catch (error) {
      console.error('Error saving rating and review:', error);
      throw error;
    }
  }, []);

  // Manual refetch function (re-setup all listeners)
  const refetch = useCallback(() => {
    setupRealtimeListeners();
  }, [setupRealtimeListeners]);

  // Function to update package selection state (optimistic + real-time)
  const updatePackageSelection = useCallback((packageId: string, selected: boolean) => {
    // Optimistic update
    setData(prevData => 
      prevData.map(pkg => 
        pkg.packageId === packageId 
          ? { ...pkg, selected } 
          : pkg
      )
    );
  }, []);

  // Setup listeners when user changes
  useEffect(() => {
    setupRealtimeListeners();

    // Cleanup function
    return () => {
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = [];
    };
  }, [setupRealtimeListeners]);

  return {
    data,
    loading,
    error,
    refetch,
    saveRatingAndReview,
    packageRatings,
    loadingRatings: false, // Always false since we use real-time
    updatePackageSelection,
    // Expose real-time data for debugging/advanced usage
    profiles,
    packages,
    purchases,
    userSelections
  };
};